// SPEC manna-spec.md §2.2.6 — subtle session-MFA hint on read-heavy pages
// (revised 2026-06-04, supersedes the 2026-05-02 "no banner" decision).
//
// An internal-role user (anything other than partner_admin / partner_rep)
// whose JWT has is_mfa_active=false has zero baseline RLS access (§2.2.5),
// so every list silently renders empty. The original §2.2.6 decision left
// read-heavy pages with NO banner — which, in practice, looked like missing
// data with no explanation (a Revenue Leader who couldn't see any deals).
//
// This component restores a SUBTLE, non-blocking hint on those off-list
// pages: a slim info alert that names the cause and deep-links to /security
// to enrol. It is intentionally NOT shown where the blocking MfaStepUpDialog
// already covers the page (allow-listed routes, via routeRequiresSessionMfa)
// or on /security itself (the elevation surface). It disappears permanently
// once the user enrols TOTP and the JWT re-mints is_mfa_active=true, so it is
// a transient nudge for a fixable state, not ambient nag for everyone.

import { Alert, Button } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/auth";
import { paths } from "../routes/paths";
import { hasInternalAppRole, routeRequiresSessionMfa } from "./RequireInternalMfa";

export function useShowMfaSessionHint(): boolean {
  const location = useLocation();
  const appRoles = useAuthStore((s) => s.appRoles);
  const isMfaActive = useAuthStore((s) => s.isMfaActive);

  const internal = hasInternalAppRole(appRoles);
  const isSecurityRoute = location.pathname === paths.security;
  // Allow-listed routes raise the blocking MfaStepUpDialog already — no hint.
  const blockingDialogCovers = routeRequiresSessionMfa(location.pathname);

  return internal && !isMfaActive && !isSecurityRoute && !blockingDialogCovers;
}

export function MfaSessionHint() {
  const show = useShowMfaSessionHint();
  if (!show) return null;

  return (
    <Alert
      severity="info"
      variant="outlined"
      data-testid="mfa-session-hint"
      sx={{ mb: 2, alignItems: "center" }}
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to={paths.security}
          data-testid="mfa-session-hint-verify"
        >
          Verify MFA
        </Button>
      }
    >
      Records stay hidden until you verify multi-factor authentication. Enrol an authenticator at
      Security to see your data.
    </Alert>
  );
}
